import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {CircleMarker, MapContainer, Polygon, TileLayer, Tooltip, useMap} from 'react-leaflet';
import {formatValidationDate} from '../SensorValidation/validationApi';
import {
    buildMomentQuery,
    buildSeriesQuery,
    cellOutline,
    communityMapRequest,
    frostColor,
    frostLegend,
    label,
    sensorStatusOrder,
    sensorStatusStyle,
    utcToLocalInput
} from './communityMapApi';
import './CommunityMapHistory.scss';

const POLAND_CENTER = [52.0, 19.4];
const statsRows = [
    ['candidates', 'Sensors considered'], ['withValue', 'With a reading'], ['included', 'Included sensors'],
    ['unknownIncluded', 'Included without assessment'], ['excludedOutlier', 'Outliers'],
    ['excludedProbation', 'On probation'], ['excludedUnknown', 'Unknown, excluded'],
    ['droppedLive', 'Dropped at this moment'], ['spikeReplaced', 'Spike replaced'], ['stale', 'Stale'], ['noData', 'No data']
];

const formatTemperature = value => (value === null || value === undefined ? '—' : `${Number(value).toFixed(1)} °C`);

/** Zooms the map to the loaded cells and sensors whenever a new data set is loaded (not on frame changes). */
const FitToData = ({loadId, cells, sensors}) => {
    const map = useMap();
    useEffect(() => {
        if (!loadId) return;
        const lats = [];
        const lons = [];
        cells.forEach(cell => {
            lats.push(cell.bounds[0], cell.bounds[2]);
            lons.push(cell.bounds[1], cell.bounds[3]);
        });
        sensors.forEach(sensor => {
            lats.push(sensor.lat);
            lons.push(sensor.lon);
        });
        if (lats.length === 0) return;
        map.fitBounds([[Math.min(...lats), Math.min(...lons)], [Math.max(...lats), Math.max(...lons)]],
            {padding: [30, 30], maxZoom: 11});
        // Only a new load moves the map; stepping through frames keeps the user's view.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadId, map]);
    return null;
};

const defaultMoment = () => {
    const date = new Date();
    date.setHours(date.getHours() - 1, 0, 0, 0);
    return utcToLocalInput(date.toISOString());
};

const CommunityMapHistory = () => {
    const t = useSelector(state => state.language.translator);
    const locale = localStorage.getItem('lang') || 'en';

    const [config, setConfig] = useState(null);
    const [level, setLevel] = useState(5);
    const [momentInput, setMomentInput] = useState(defaultMoment());
    const [seriesForm, setSeriesForm] = useState({from: '', to: '', stepMinutes: 30});
    const [moment, setMoment] = useState(null);
    const [frames, setFrames] = useState(null);
    const [frameIndex, setFrameIndex] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [showSensors, setShowSensors] = useState(true);
    const [showCells, setShowCells] = useState(true);
    const [selectedSensor, setSelectedSensor] = useState(null);
    const [assessments, setAssessments] = useState(null);
    const [pending, setPending] = useState(false);
    const [error, setError] = useState(null);
    const [loadId, setLoadId] = useState(0);

    useEffect(() => {
        communityMapRequest('/panel/community_map/config')
            .then(setConfig)
            .catch(() => setError(t.gettext('Could not load the map configuration.')));
    }, [t]);

    const run = useCallback((request, onSuccess) => {
        setPending(true);
        setError(null);
        return request()
            .then(onSuccess)
            .catch(e => setError(e.message && !/^\d+$/.test(e.message)
                ? t.gettext(e.message)
                : `${t.gettext('The server could not build the map.')} (${e.message})`))
            .finally(() => setPending(false));
    }, [t]);

    const loadMoment = () => {
        let query;
        try {
            query = buildMomentQuery(momentInput, level);
        } catch (e) {
            setError(t.gettext(e.message));
            return;
        }
        run(() => communityMapRequest(query), result => {
            setMoment(result);
            setFrames(null);
            setSelectedSensor(null);
            setAssessments(null);
            setLoadId(id => id + 1);
        });
    };

    const loadSeries = () => {
        let query;
        try {
            query = buildSeriesQuery({...seriesForm, level}, config ? config.maxSeriesSteps : 96);
        } catch (e) {
            setError(t.gettext(e.message));
            return;
        }
        run(() => communityMapRequest(query), result => {
            setFrames(result.frames);
            setFrameIndex(0);
            setMoment(null);
            setPlaying(false);
            setLoadId(id => id + 1);
        });
    };

    const loadFrameDetails = () => {
        const frame = frames && frames[frameIndex];
        if (!frame) return;
        run(() => communityMapRequest(buildMomentQuery(utcToLocalInput(frame.at), level)), result => {
            setMoment(result);
        });
    };

    const selectSensor = sensor => {
        setSelectedSensor(sensor);
        setAssessments(null);
        const day = new Date((current && current.assessmentDay) || Date.now());
        const from = new Date(day.getTime() - 14 * 86400000).toISOString().slice(0, 10);
        const to = day.toISOString().slice(0, 10);
        communityMapRequest(`/panel/community_map/assessments?sensorId=${sensor.id}&from=${from}&to=${to}`)
            .then(setAssessments)
            .catch(() => setAssessments([]));
    };

    useEffect(() => {
        if (!playing || !frames) return undefined;
        const timer = setInterval(() => {
            setFrameIndex(index => (index + 1 < frames.length ? index + 1 : 0));
        }, 900);
        return () => clearInterval(timer);
    }, [playing, frames]);

    const current = useMemo(() => {
        if (frames) {
            const frame = frames[frameIndex];
            return moment && frame && moment.at === frame.at ? moment : frame;
        }
        return moment;
    }, [frames, frameIndex, moment]);

    const cells = (current && current.levels && (current.levels[level] || Object.values(current.levels)[0])) || [];
    const sensors = (current && current.sensors) || [];
    const levels = config ? Object.keys(config.levelCellSizeKm).map(Number) : [3, 4, 5, 6];

    return (
        <div className='CommunityMapHistory'>
            <div className='communityMapPanel'>
                <div className='communityMapHeader'>
                    <span>{t.gettext('Community temperature map – history')}</span>
                    {config && <span className='communityMapVersion'>{t.gettext('Algorithm')} {config.algorithmVersion}</span>}
                </div>

                <div className='communityMapControls'>
                    <fieldset>
                        <legend>{t.gettext('Single moment')}</legend>
                        <label>{t.gettext('Moment')}
                            <input type='datetime-local' value={momentInput} onChange={e => setMomentInput(e.target.value)}/>
                        </label>
                        <label>{t.gettext('Level')}
                            <select value={level} onChange={e => setLevel(Number(e.target.value))}>
                                {levels.map(value => (
                                    <option key={value} value={value}>
                                        {`L${value}${config ? ` (~${config.levelCellSizeKm[value]} km)` : ''}`}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <button type='button' className='communityMapButton' disabled={pending} onClick={loadMoment}>
                            {t.gettext('Show map')}
                        </button>
                    </fieldset>
                    <fieldset>
                        <legend>{t.gettext('Series')}</legend>
                        <label>{t.gettext('From')}
                            <input type='datetime-local' value={seriesForm.from}
                                   onChange={e => setSeriesForm({...seriesForm, from: e.target.value})}/>
                        </label>
                        <label>{t.gettext('To')}
                            <input type='datetime-local' value={seriesForm.to}
                                   onChange={e => setSeriesForm({...seriesForm, to: e.target.value})}/>
                        </label>
                        <label>{t.gettext('Step')}
                            <select value={seriesForm.stepMinutes}
                                    onChange={e => setSeriesForm({...seriesForm, stepMinutes: Number(e.target.value)})}>
                                {[15, 30, 60, 120].map(step => <option key={step} value={step}>{step} min</option>)}
                            </select>
                        </label>
                        <button type='button' className='communityMapButton' disabled={pending} onClick={loadSeries}>
                            {t.gettext('Load series')}
                        </button>
                    </fieldset>
                    <fieldset className='communityMapToggles'>
                        <legend>{t.gettext('Layers')}</legend>
                        <label className='communityMapCheckbox'>
                            <input type='checkbox' checked={showCells} onChange={() => setShowCells(!showCells)}/>
                            {t.gettext('Show cells')}
                        </label>
                        <label className='communityMapCheckbox'>
                            <input type='checkbox' checked={showSensors} onChange={() => setShowSensors(!showSensors)}/>
                            {t.gettext('Show sensors')}
                        </label>
                    </fieldset>
                </div>

                {error && <div className='communityMapError' role='alert'>{error}</div>}
                {pending && <div className='communityMapPending'>{t.gettext('Building the map – the first run for a day can take a while…')}</div>}

                {frames && (
                    <div className='communityMapTimeline'>
                        <button type='button' className='communityMapIconButton' onClick={() => setPlaying(!playing)}
                                aria-label={playing ? t.gettext('Pause') : t.gettext('Play')}>
                            <span className='material-icons'>{playing ? 'pause' : 'play_arrow'}</span>
                        </button>
                        <input type='range' min={0} max={frames.length - 1} value={frameIndex} aria-label={t.gettext('Frame')}
                               onChange={e => { setPlaying(false); setFrameIndex(Number(e.target.value)); }}/>
                        <span className='communityMapFrameLabel'>
                            {`${frameIndex + 1} / ${frames.length}`}
                        </span>
                        <span>{formatValidationDate(frames[frameIndex] && frames[frameIndex].at, locale)}</span>
                        <button type='button' className='communityMapButton communityMapButton--secondary'
                                disabled={pending} onClick={loadFrameDetails}>
                            {t.gettext('Sensors for this frame')}
                        </button>
                    </div>
                )}

                <div className='communityMapBody'>
                    <div className='communityMapMap'>
                        <MapContainer center={POLAND_CENTER} zoom={6} scrollWheelZoom={true}>
                            <TileLayer url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                                       attribution='&copy; OpenStreetMap contributors'/>
                            <FitToData loadId={loadId} cells={cells} sensors={sensors}/>
                            {showCells && cells.map(cell => (
                                <Polygon key={cell.id}
                                           positions={cellOutline(cell)}
                                           pathOptions={{color: '#555555', weight: 1, fillColor: frostColor(cell.value), fillOpacity: 0.6}}>
                                    <Tooltip>
                                        <div><strong>{formatTemperature(cell.value)}</strong> ({label(t, cell.statistic)})</div>
                                        <div>{t.gettext('Median')}: {formatTemperature(cell.median)}</div>
                                        <div>{t.gettext('Sensors')}: {cell.sensorCount}, {t.gettext('owners')}: {cell.siteCount}</div>
                                        {cell.robustMinApplied && <div>{t.gettext('Lowest value not confirmed – second lowest shown')}</div>}
                                    </Tooltip>
                                </Polygon>
                            ))}
                            {showSensors && sensors.map(sensor => {
                                const style = sensorStatusStyle(sensor.status);
                                return (
                                    <CircleMarker key={sensor.id} center={[sensor.lat, sensor.lon]} radius={4}
                                                  pathOptions={{color: '#ffffff', weight: 1, fillColor: style.color, fillOpacity: 0.95}}
                                                  eventHandlers={{click: () => selectSensor(sensor)}}>
                                        <Tooltip>
                                            <div><strong>{sensor.name}</strong></div>
                                            <div>{formatTemperature(sensor.value)} · {t.gettext(style.label)}</div>
                                        </Tooltip>
                                    </CircleMarker>
                                );
                            })}
                        </MapContainer>
                    </div>

                    <div className='communityMapSide'>
                        {current && (
                            <div className='communityMapBox'>
                                <h4>{formatValidationDate(current.at, locale)}</h4>
                                <div className='communityMapMuted'>
                                    {t.gettext('Assessment day')}: {current.assessmentDay} · {cells.length} {t.gettext('cells')}
                                </div>
                                <table className='communityMapStats'>
                                    <tbody>
                                    {statsRows.map(([key, text]) => (
                                        <tr key={key}><td>{t.gettext(text)}</td><td>{current.stats ? current.stats[key] : '—'}</td></tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {selectedSensor && (
                            <div className='communityMapBox'>
                                <h4>{selectedSensor.name} <span className='communityMapMuted'>#{selectedSensor.id}</span></h4>
                                <button type='button' className='communityMapLink'
                                        onClick={() => window.open(`/community_sensor/${selectedSensor.id}`, '_blank')}>
                                    {t.gettext('Open sensor history (new tab)')}
                                </button>
                                <table className='communityMapStats'>
                                    <tbody>
                                    <tr><td>{t.gettext('Status')}</td><td>{t.gettext(sensorStatusStyle(selectedSensor.status).label)}</td></tr>
                                    <tr><td>{t.gettext('Value')}</td><td>{formatTemperature(selectedSensor.value)}</td></tr>
                                    <tr><td>{t.gettext('Reading time')}</td><td>{formatValidationDate(selectedSensor.readingTime, locale)}</td></tr>
                                    <tr><td>{t.gettext('Trust state')}</td><td>{label(t, selectedSensor.historyState)}</td></tr>
                                    <tr><td>{t.gettext('Reason')}</td><td>{label(t, selectedSensor.decisionReason)}</td></tr>
                                    <tr><td>{t.gettext('Last outlier day')}</td><td>{selectedSensor.lastOutlierDay || '—'}</td></tr>
                                    <tr><td>{t.gettext('Deviation from neighbours now')}</td>
                                        <td>{selectedSensor.liveDeviation === null || selectedSensor.liveDeviation === undefined
                                            ? '—' : formatTemperature(selectedSensor.liveDeviation)}</td></tr>
                                    </tbody>
                                </table>
                                <h4>{t.gettext('Assessment history')}</h4>
                                {assessments === null
                                    ? <div className='communityMapMuted'>{t.gettext('Loading…')}</div>
                                    : (
                                        <table className='communityMapHistoryTable'>
                                            <thead>
                                            <tr><th>{t.gettext('Day')}</th><th>{t.gettext('Assessment')}</th>
                                                <th>{t.gettext('Trust state')}</th><th>{t.gettext('Reason')}</th></tr>
                                            </thead>
                                            <tbody>
                                            {assessments.map(row => (
                                                <tr key={row.asOfDay} title={(row.flags || []).map(flag => label(t, flag)).join(', ')}>
                                                    <td>{row.asOfDay}</td>
                                                    <td>{label(t, row.status)}</td>
                                                    <td>{label(t, row.historyState)}</td>
                                                    <td>{label(t, row.decisionReason)}</td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    )}
                            </div>
                        )}
                        <div className='communityMapBox'>
                            <h4>{t.gettext('Legend')}</h4>
                            <div className='communityMapLegend'>
                                {frostLegend.map(item => (
                                    <span key={item.label}><i style={{background: item.color}}/>{item.label}</span>
                                ))}
                            </div>
                            <div className='communityMapLegend'>
                                {sensorStatusOrder.map(status => (
                                    <span key={status}>
                                        <i className='communityMapDot' style={{background: sensorStatusStyle(status).color}}/>
                                        {t.gettext(sensorStatusStyle(status).label)}
                                    </span>
                                ))}
                            </div>
                            <div className='communityMapMuted'>
                                {t.gettext('Cells show the lowest value when the cell median is at or below')} {config ? config.frostThreshold : 4} °C,
                                {' '}{t.gettext('otherwise the median. Sensor points are visible to super admins only.')}
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default CommunityMapHistory;
